import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { ClassName, GADataType, I18nText } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { MaterialsSetUUID } from '../../../materialsSetsTypes';

import {
  FETCH_MATERIALS_SET_DOWNLOAD_URLS_QUERY,
  FetchMaterialsSetDownloadUrlsQueryResponse
} from '../../../queries/fetchMaterialsSetDownloadUrls.query';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useMaterialsSet } from '../../../hooks/useMaterialsSet';
import { useShowToastOnErrorChange } from '../../../../../common/hooks/useShowToastOnErrorChange';
import { useToastNotification } from '../../../../../common/hooks/useToastNotification';

import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { TooltipPlacement } from '../../../../../helpers/tooltips/tooltipsConstants';

import { downloadFile } from '../../../../../utils/downloadFile';
import { getFileNameFromURL } from '../../../../../utils/getFileNameFromURL';

import { MaterialsSetCache } from '../../../MaterialsSetCache';

import { words } from '../../../../../locales/keys';
import { CommonPermissions } from '../../../../common/commonConstants';

interface DownloadMaterialsSetSeparatelyButtonProps {
  className?: ClassName;
  i18nText?: I18nText;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  materialsSetUuid: MaterialsSetUUID;
  tooltipI18nText?: I18nText;
  tooltipPlacement?: TooltipPlacement;
  tooltipSingleton?: boolean;
}

function DownloadMaterialsSetSeparatelyButton({
  className,
  dataGa,
  i18nText,
  icon,
  iconClassName,
  materialsSetUuid,
  tooltipI18nText,
  tooltipPlacement,
  tooltipSingleton
}: DownloadMaterialsSetSeparatelyButtonProps & GADataType) {
  const currentUser = useCurrentUser();

  const { materialsSetLoading, refetchMaterialsSet, materialsSetError } =
    useMaterialsSet<FetchMaterialsSetDownloadUrlsQueryResponse>({
      cacheKey: MaterialsSetCache.downloadUrls(materialsSetUuid),
      options: {
        enabled: false,
        enabledPlaceholder: false
      },
      query: FETCH_MATERIALS_SET_DOWNLOAD_URLS_QUERY,
      uuid: materialsSetUuid
    });

  const { showToastI18nNotification } = useToastNotification({
    appearance: 'success',
    i18nMessage: words.fileDownloadStarted
  });

  const withBlob = currentUser.hasPermissions(
    CommonPermissions.READ_DOWNLOADS_WITH_FETCH
  );

  useShowToastOnErrorChange({ error: materialsSetError });

  const handleClick = useCallback<() => Promise<void>>(async () => {
    const refetchMaterialsSetResponse = await refetchMaterialsSet();

    const downloadUrls =
      refetchMaterialsSetResponse?.data?.materialsSet?.downloadUrls;

    if (isEmpty(downloadUrls)) {
      return;
    }

    showToastI18nNotification();

    await Promise.all(
      map(downloadUrls, ({ url }) =>
        downloadFile({
          downloadFile: {
            name: getFileNameFromURL(url),
            file: url
          },
          withBlob
        })
      )
    );
  }, [refetchMaterialsSet, showToastI18nNotification, withBlob]);

  return (
    <PureTooltipIconButtonHelper
      className={className}
      dataGa={dataGa}
      disabled={materialsSetLoading}
      i18nText={i18nText}
      icon={icon}
      iconClassName={iconClassName}
      onClick={handleClick}
      tooltipI18nText={tooltipI18nText}
      tooltipPlacement={tooltipPlacement}
      tooltipSingleton={tooltipSingleton}
    />
  );
}

export default DownloadMaterialsSetSeparatelyButton;
